// Saisie d'un champ « tableau de lignes » : une ligne par enregistrement, colonnes typées (texte,
// nombre, montant, date, choix unique, oui ou non), ajout et retrait de lignes, erreurs par cellule.
import type { Champ, Colonne } from '@cc/contrats';
import { Bouton } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useId } from 'react';
import styles from './formulaire.module.css';

type Ligne = Record<string, unknown>;

export interface ProprietesChampTableau {
  champ: Champ;
  lignes: Ligne[];
  onChanger: (lignes: Ligne[]) => void;
  /** Erreurs dont le chemin est « code.index.colonne ». */
  erreurs: { champ: string; message: string }[];
  disabled?: boolean;
}

function valeurCellule(k: Colonne, brut: string): unknown {
  if (brut === '') return undefined;
  if (k.type === 'nombre' || k.type === 'montant') {
    const n = Number(brut.replace(',', '.'));
    return Number.isNaN(n) ? brut : n;
  }
  if (k.type === 'oui_non') return brut === 'oui';
  return brut;
}

function texteCellule(k: Colonne, v: unknown): string {
  if (v === undefined || v === null) return '';
  if (k.type === 'oui_non') return v === true ? 'oui' : v === false ? 'non' : '';
  return String(v);
}

export function ChampTableau({ champ, lignes, onChanger, erreurs, disabled }: ProprietesChampTableau) {
  const base = useId();
  const colonnes = champ.colonnes ?? [];
  const modifier = (i: number, k: Colonne, brut: string) => {
    const copie = lignes.map((l, j) => (j === i ? { ...l, [k.code]: valeurCellule(k, brut) } : l));
    onChanger(copie);
  };
  const erreurCellule = (i: number, code: string) => erreurs.find((e) => e.champ === `${champ.code}.${i}.${code}`)?.message;
  return (
    <div className={styles.tableau}>
      <table>
        <caption className="cc-masque">{champ.libelle}</caption>
        <thead>
          <tr>
            {colonnes.map((k) => (
              <th key={k.code} scope="col">
                {k.libelle}
                {k.obligatoire ? <span aria-hidden> *</span> : null}
              </th>
            ))}
            <th scope="col">
              <span className="cc-masque">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 ? (
            <tr>
              <td colSpan={colonnes.length + 1} className={styles.vide}>
                Aucune ligne.
              </td>
            </tr>
          ) : null}
          {lignes.map((l, i) => (
            <tr key={i}>
              {colonnes.map((k) => {
                const id = `${base}-${i}-${k.code}`;
                const erreur = erreurCellule(i, k.code);
                const commun = { id, disabled, 'aria-label': `${k.libelle}, ligne ${i + 1}`, 'aria-invalid': erreur ? true : undefined, 'aria-describedby': erreur ? `${id}-erreur` : undefined };
                return (
                  <td key={k.code}>
                    {k.type === 'choix_unique' ? (
                      <select {...commun} value={texteCellule(k, l[k.code])} onChange={(e) => modifier(i, k, e.target.value)}>
                        <option value="">Choisir</option>
                        {(k.options ?? []).map((o) => (
                          <option key={o.valeur} value={o.valeur}>
                            {o.libelle}
                          </option>
                        ))}
                      </select>
                    ) : k.type === 'oui_non' ? (
                      <select {...commun} value={texteCellule(k, l[k.code])} onChange={(e) => modifier(i, k, e.target.value)}>
                        <option value="">Choisir</option>
                        <option value="oui">Oui</option>
                        <option value="non">Non</option>
                      </select>
                    ) : (
                      <input
                        {...commun}
                        type={k.type === 'date' ? 'date' : k.type === 'nombre' || k.type === 'montant' ? 'number' : 'text'}
                        step={k.type === 'montant' ? 1 : k.type === 'nombre' ? 'any' : undefined}
                        value={texteCellule(k, l[k.code])}
                        onChange={(e) => modifier(i, k, e.target.value)}
                      />
                    )}
                    {erreur ? (
                      <span id={`${id}-erreur`} className={styles.erreurCellule}>
                        {erreur}
                      </span>
                    ) : null}
                  </td>
                );
              })}
              <td>
                <Bouton petit variante="discret" icone={Trash2} disabled={disabled} aria-label={`Retirer la ligne ${i + 1}`} onClick={() => onChanger(lignes.filter((_, j) => j !== i))}>
                  Retirer
                </Bouton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Bouton petit variante="secondaire" icone={Plus} disabled={disabled} onClick={() => onChanger([...lignes, {}])}>
        Ajouter une ligne
      </Bouton>
    </div>
  );
}
